// insight ของฉัน — ดู insight ของวันนี้และย้อนหลัง 60 วัน (เฉพาะของตัวเอง) · ไฟล์แนบของวันก่อน ๆ เก็บ 90 วัน
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import api from '../adapters/api';
import InsightCard from '../components/insights/InsightCard';
import InsightRowsDrawer, { rowActionFor } from '../components/insights/InsightRowsDrawer';
import useInsightTasks, { logInsightEvent } from '../components/insights/useInsightTasks';
import { useInsightMe } from '../components/insights/useInsightInline';

const THAI_DAYS = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const dayLabel = iso => {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${THAI_DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${THAI_MONTHS[d.getUTCMonth()]}`;
};
const todayBangkok = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const STATUS_TEXT = {
  pending: 'insight ของวันนี้จะพร้อมหลัง 06:00 น.',
  running: 'กำลังเตรียม insight ของวันนี้…',
  not_ready: 'insight ของวันนี้ยังไม่พร้อม (ระบบกำลังตรวจสอบ)',
  holiday: 'วันนี้เป็นวันหยุดของบริษัท',
};

export default function MyTasks() {
  const [date, setDate] = useState(null); // null = วันนี้ (ใช้ cache ร่วมกับแถบหน้าแรก/💡)
  const [rowsTask, setRowsTask] = useState(null);
  const { data: me, isLoading: meLoading } = useInsightMe();
  const enabled = Boolean(me?.personas?.length);
  const { data: history = [] } = useQuery({
    queryKey: ['insightHistory'],
    queryFn: async () => (await api.get('/api/insight/history')).data,
    enabled,
  });
  const { data: daily, isLoading, actionsFor, readOnly } = useInsightTasks(enabled, date);

  if (meLoading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;
  if (!enabled) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="info">ระบบ insight ยังไม่ได้เปิดใช้สำหรับบัญชีนี้</Alert>
      </Box>
    );
  }

  const tasks = daily?.tasks || [];
  const isPast = Boolean(date);
  const handled = tasks.filter(t => t.status !== 'open').length;
  const pastDays = history.filter(h => h.date !== todayBangkok());

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h6" fontWeight={700}>insight ของฉัน</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        เรื่องที่ระบบวิเคราะห์จากข้อมูลจริงให้ในแต่ละวัน — กด 👍/👎 เพื่อช่วยให้ระบบเลือกเรื่องที่มีประโยชน์กับคุณมากขึ้น
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        <Chip label="วันนี้" color={!date ? 'primary' : 'default'} onClick={() => setDate(null)} />
        {pastDays.map(h => (
          <Chip
            key={h.date}
            variant={date === h.date ? 'filled' : 'outlined'}
            color={date === h.date ? 'primary' : 'default'}
            label={`${dayLabel(h.date)} · ${h.total} เรื่อง${h.done + h.skipped ? ` (จัดการ ${h.done + h.skipped})` : ''}`}
            onClick={() => setDate(h.date)}
          />
        ))}
      </Box>

      {isLoading && <CircularProgress size={24} />}
      {daily && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {isPast ? dayLabel(daily.date) : 'วันนี้'} · {tasks.length} เรื่อง{tasks.length ? ` · จัดการแล้ว ${handled}` : ''}
          </Typography>
          {!tasks.length && (
            <Typography variant="body2" color="text.secondary">
              {(!isPast && STATUS_TEXT[daily.status]) || 'ไม่มีเรื่องที่ควรดูในวันนี้'}
            </Typography>
          )}
          {isPast && tasks.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              ตัวเลขเป็นข้อมูล ณ วันนั้น · รายการ/ไฟล์แนบเก็บไว้ 90 วัน
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tasks.map(t => (
              <InsightCard key={t.id} task={t} actions={actionsFor(t, { onOpenRows: setRowsTask })} />
            ))}
          </Box>
        </>
      )}
      <InsightRowsDrawer
        key={rowsTask?.id || 'none'}
        open={Boolean(rowsTask)} onClose={() => setRowsTask(null)}
        url={rowsTask ? `/api/insight/tasks/${rowsTask.id}/rows` : null}
        title={rowsTask?.title}
        onDownload={rowsTask ? () => actionsFor(rowsTask).download() : undefined}
        onCopy={rowsTask ? () => logInsightEvent(rowsTask.id, 'copy') : undefined}
        rowAction={isPast ? undefined : rowActionFor(rowsTask, readOnly)}
      />
    </Box>
  );
}
